import { createFileRoute } from "@tanstack/react-router";
import { apiFetch, clientRequestHeaders, ensureCsrfToken } from "@/lib/sm-api";
import { useEffect, useState } from "react";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, XCircle, CircleDashed } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { AIHealthEntry } from "@/lib/ai/types";

export const Route = createFileRoute("/admin/ops/ai/health")({
  component: AdminAIHealthPage,
  head: () => ({ meta: [{ title: "Salud IA — Admin" }] }),
});

function StatusIcon({ status }: { status: string }) {
  if (status === "healthy") return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (status === "degraded") return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
  if (status === "down") return <XCircle className="h-4 w-4 text-red-500" />;
  return <CircleDashed className="h-4 w-4 text-muted-foreground" />;
}

function AdminAIHealthPage() {
  const [health, setHealth] = useState<AIHealthEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/ai/health", { credentials: "include" });
      const data = await res.json() as { health: AIHealthEntry[] };
      setHealth(data.health ?? []);
    } finally {
      setLoading(false);
    }
  }

  async function ping(provider: string) {
    setPinging(provider);
    try {
      await apiFetch("/api/admin/ai/health", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider }),
      });
      await load();
    } finally {
      setPinging(null);
    }
  }

  useEffect(() => { void load(); }, []);

  function formatLatency(ms: number | null) {
    if (ms === null) return "—";
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString("es-AR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit", hour12: false });
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">AI Health</h1>
          <p className="text-sm text-muted-foreground">Estado de los proveedores AI registrados.</p>
        </div>
        <Button size="sm" variant="outline" className="gap-2" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </Button>
      </div>

      {loading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Proveedor</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Modelo</th>
                <th className="px-4 py-2 text-center font-medium text-muted-foreground">Estado</th>
                <th className="px-4 py-2 text-right font-medium text-muted-foreground">Latencia</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Último check</th>
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">Error</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {health.map((h) => (
                <tr key={h.provider} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-4 py-2 font-medium">{h.provider}</td>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{h.model ?? "—"}</td>
                  <td className="px-4 py-2">
                    <div className="flex justify-center">
                      <StatusIcon status={h.status} />
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatLatency(h.latencyMs)}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{formatDate(h.lastChecked)}</td>
                  <td className="px-4 py-2 text-xs text-red-600 max-w-xs truncate">{h.error ?? "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={pinging === h.provider}
                      onClick={() => ping(h.provider)}
                    >
                      {pinging === h.provider ? <Loader2 className="h-3 w-3 animate-spin" /> : "Ping"}
                    </Button>
                  </td>
                </tr>
              ))}
              {health.length === 0 && (
                <tr><td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">Sin registros de salud aún.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
